import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { Event } from '@models/event.model';
import { Task } from '@models/task.model';
import { Accordion as AccordionUsage } from './components';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { useTask } from '@hooks/api/task/useTask';
import { useProjects } from '@hooks/api/projects/useProjects';
import { CreateEditTaskModal } from '@components/CreateEditTaskModal';
import { useProject } from '@hooks/api/projects/useProject';
import { useTasks } from '@hooks/api/task/useTasks';
import { useEvents, useEvent } from '@hooks/api';
import { CreateEditEventModal } from '@components/CreateEditTaskModal/components/CreateEditEventModal';
import { TaskDetailModal } from '@components/organisms/TaskDetailModal';
import Legend from './components/Legend/Legend';
import { useUpdateStatusProject } from '@hooks/api/projects/useUpdateStatusProject';
import { TaskStatus } from '@models/task.model';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import ProjectsList from './components/ProjectsList';
import { SelectTasksModal } from '@components/organisms/SelectTasksModal';
import { CreateTaskTemplateModal } from '@components/organisms/CreateTaskTemplateModal';
import { ImportTaskTemplateModal } from '@components/organisms/ImportTaskTemplateModal';
import { TaskCategoriesSlideModal } from '@components/organisms/TaskCategoriesSlideModal';
import { Button } from '@components/atoms/Button';
import { useAppSelector } from '@redux/hooks';
import { UserRoles } from '@models/user.model';

const TASK_CATEGORY_WRITE_ROLES: string[] = [
  UserRoles.SUPER_ADMIN,
  UserRoles.OWNER,
  UserRoles.ADMIN,
  UserRoles.PROJECT_MANAGER,
  UserRoles.SUPERINTENDENT,
];

const COMPLETION_DISMISSALS_KEY = 'completedProjectPromptDismissals';

/** Identifies a project's task set, so a dismissed prompt is not repeated until the set changes. */
const getTaskSetSignature = (taskIds: string[]): string =>
  `${taskIds.length}:${[...taskIds].sort().join(',')}`;

const readDismissals = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(COMPLETION_DISMISSALS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.error('Error reading prompt dismissals from localStorage:', error);
    return {};
  }
};

const getDismissedSignature = (projectId: string): string | undefined =>
  readDismissals()[projectId];

const setDismissedSignature = (projectId: string, signature: string) => {
  try {
    localStorage.setItem(
      COMPLETION_DISMISSALS_KEY,
      JSON.stringify({ ...readDismissals(), [projectId]: signature }),
    );
  } catch (error) {
    console.error('Error saving prompt dismissal to localStorage:', error);
  }
};

interface RouteParams {
  id: string;
  /** Optional task to open on arrival — set by "View in Project". */
  taskId?: string;
}

const SchedulePage: React.FC = () => {
  const [showNewSubtaskModal, setShowNewSubtaskModal] =
    useState<boolean>(false);
  const [subtaskParentTask, setSubtaskParentTask] = useState<Task | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const { id, taskId: taskIdFromUrl } = useParams<RouteParams>();
  const [showNewTaskModal, setShowNewTaskModal] = useState<boolean>(false);
  const [showTaskDetailModal, setShowTaskDetailModal] =
    useState<boolean>(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [showSelectTasksModal, setShowSelectTasksModal] =
    useState<boolean>(false);
  const [showCreateTemplateModal, setShowCreateTemplateModal] =
    useState<boolean>(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [templateProjectId, setTemplateProjectId] = useState<string | null>(
    null,
  );
  const [showImportTemplateModal, setShowImportTemplateModal] =
    useState<boolean>(false);
  const [showTaskCategoriesModal, setShowTaskCategoriesModal] =
    useState<boolean>(false);

  const userRole = useAppSelector((state) => state.user.role);
  const canManageTaskCategories = useMemo(
    () => !!userRole && TASK_CATEGORY_WRITE_ROLES.includes(userRole),
    [userRole],
  );

  // hooks
  const { data: projects = [] } = useProjects();
  const { data: project } = useProject(id || projectId || undefined);
  const { task, fetchTask, setTask } = useTask();
  const { tasks, fetchTasks } = useTasks(projectId || id || undefined);
  const { events: fetchedEvents, fetchEvents } = useEvents();
  const { event, fetchEvent, setEvent } = useEvent();
  const updateStatusProjectMutation = useUpdateStatusProject();

  useEffect(() => {
    if (id || projectId) {
      fetchTasks(id || projectId || '');
    }
    if (task?.projectId) {
      fetchTasks(task.projectId);
      setProjectId(task.projectId);
    }
  }, [id, projectId, task, fetchTasks]);

  useEffect(() => {
    if (projectId) {
      fetchEvents(projectId);
    }
  }, [projectId, fetchEvents]);

  useEffect(() => {
    if (fetchedEvents) {
      setEvents(fetchedEvents);
    }
  }, [fetchedEvents]);

  // Sprint 51, story-261: this page is the ONLY owner of the Complete Project prompt.
  // It fires when a project's tasks go from "not all complete" to "all complete" while this page
  // is watching (a task entering COMPLETE) - never on first load or navigation - and never while
  // a task/event/template modal is open.
  const projectTasks = useMemo(
    () =>
      project?.projectId
        ? (tasks || []).filter((t) => t.projectId === project.projectId)
        : [],
    [tasks, project?.projectId],
  );
  const taskSetSignature = useMemo(
    () =>
      getTaskSetSignature(projectTasks.map((t) => t.taskId as string)),
    [projectTasks],
  );
  const prevAllCompleteRef = useRef<Map<string, boolean>>(new Map());
  const [completionPending, setCompletionPending] = useState<boolean>(false);
  const [dismissedTick, setDismissedTick] = useState<number>(0);

  const isAnotherModalOpen =
    showNewTaskModal ||
    showTaskDetailModal ||
    showNewSubtaskModal ||
    isEventModalOpen ||
    showSelectTasksModal ||
    showCreateTemplateModal ||
    showImportTemplateModal ||
    showTaskCategoriesModal;

  useEffect(() => {
    if (!id || !project?.projectId) return;
    if (project.state?.toLowerCase() === 'completed') {
      setCompletionPending(false);
      return;
    }
    // "Loaded" means this project has tasks; seeding on a transient empty list would make the
    // arrival of an all-complete list look like a false-to-true flip.
    if (projectTasks.length === 0) return;

    const allComplete = projectTasks.every(
      (t) => t.status === TaskStatus.COMPLETE,
    );
    const prev = prevAllCompleteRef.current.get(project.projectId);
    if (prev === undefined) {
      prevAllCompleteRef.current.set(project.projectId, allComplete);
    } else if (!prev && allComplete) {
      prevAllCompleteRef.current.set(project.projectId, true);
      setCompletionPending(true);
    } else if (prev && !allComplete) {
      prevAllCompleteRef.current.set(project.projectId, false);
      setCompletionPending(false);
    }
  }, [id, project?.projectId, project?.state, projectTasks]);

  const showProjectCompletionModal =
    completionPending &&
    !isAnotherModalOpen &&
    !!project?.projectId &&
    // dismissedTick forces re-evaluation after a dismissal is written
    dismissedTick >= 0 &&
    getDismissedSignature(project.projectId) !== taskSetSignature;

  const handleProjectCompletion = () => {
    if (project?.projectId) {
      // A failure keeps the prompt open (the hook shows its error toast).
      updateStatusProjectMutation.mutate(
        { projectId: project.projectId, status: 'COMPLETED' },
        { onSuccess: () => setCompletionPending(false) },
      );
    }
  };

  const handleProjectCompletionCancel = () => {
    if (project?.projectId) {
      setDismissedSignature(project.projectId, taskSetSignature);
    }
    setCompletionPending(false);
    setDismissedTick((n) => n + 1);
  };

  const toggleNewTaskModal = (projectId: string) => {
    setProjectId(projectId);
    setShowNewTaskModal(true);
    setTask(null);
    setEvent(null);
  };

  const toggleNewEventModal = (projectId: string) => {
    setProjectId(projectId);
    setIsEventModalOpen(true);
    setEvent(null);
  };

  const handleSetTask = useCallback(
    (id: string) => {
      fetchTask(id);
      setShowNewTaskModal(true);
    },
    [fetchTask],
  );

  const handleViewTaskDetails = (id: string) => {
    fetchTask(id);
    setShowTaskDetailModal(true);
  };

  // Arriving from "View in Project" opens the task straight into edit. Guarded
  // by a ref so closing the modal doesn't immediately reopen it — the URL still
  // carries the task id for as long as the user stays on the page.
  const openedFromUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!taskIdFromUrl || openedFromUrlRef.current === taskIdFromUrl) return;
    openedFromUrlRef.current = taskIdFromUrl;
    handleSetTask(taskIdFromUrl);
  }, [taskIdFromUrl, handleSetTask]);

  const handleSetEvent = (id: string) => {
    fetchEvent(id);
    setIsEventModalOpen(true);
  };

  const handleEventSuccess = () => {
    setIsEventModalOpen(false);
    setEvent(null);
    if (projectId) {
      fetchEvents(projectId);
      fetchTasks(projectId);
    }
  };

  const successTask = () => {
    if (projectId || id) {
      fetchTasks((projectId || id) as string);
    }
    if (task?.taskId) {
      fetchTask(task.taskId);
    }
    setShowNewTaskModal(false);
  };

  const successSubtask = () => {
    if (projectId) {
      fetchTasks(projectId);
    }
    if (task?.taskId) {
      fetchTask(task.taskId);
    }
    setShowNewSubtaskModal(false);
    setSubtaskParentTask(null);
  };

  const handleCreateSubtask = (parentTask: Task) => {
    setSubtaskParentTask(parentTask);
    setShowNewSubtaskModal(true);
  };

  const toggleCreateTemplateModal = (projectIdParam: string) => {
    setTemplateProjectId(projectIdParam);
    setShowSelectTasksModal(true);
  };

  const toggleImportTemplateModal = (projectIdParam: string) => {
    setTemplateProjectId(projectIdParam);
    setShowImportTemplateModal(true);
  };

  const handleTasksSelected = (taskIds: string[]) => {
    setSelectedTaskIds(taskIds);
    setShowSelectTasksModal(false);
    setShowCreateTemplateModal(true);
  };

  const handleTemplateCreated = () => {
    setShowCreateTemplateModal(false);
    setSelectedTaskIds([]);
    setTemplateProjectId(null);
  };

  return (
    <div className="flex-1 w-full pb-10">
      <Legend showToggle={true} isCollapsed={true} />

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarMonthIcon
            sx={{ fontSize: 28 }}
            className="text-orange-400 dark:text-orange-300"
          />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Schedule
          </h1>
        </div>
        <div className="flex items-center gap-2 mb-4">
        {canManageTaskCategories && (
          <Button
            variant="secondary"
            onClick={() => setShowTaskCategoriesModal(true)}
            leftIcon={<CategoryOutlinedIcon className="w-4 h-4" />}
          >
            Manage Task Categories
          </Button>
        )}
        </div>
      </div>

      {/* Show ProjectsList when no projectId is provided in the URL */}
      {!id ? (
        <ProjectsList />
      ) : (
        /* Show existing Accordion when projectId is provided */
        <AccordionUsage
          events={events}
          tasks={tasks}
          projects={projects}
          projectIdToExpand={id}
          setTask={handleSetTask}
          setEvent={handleSetEvent}
          setTaskDetails={handleViewTaskDetails}
          toggleNewTaskModal={toggleNewTaskModal}
          toggleNewEventModal={toggleNewEventModal}
          toggleCreateTemplateModal={toggleCreateTemplateModal}
          toggleImportTemplateModal={toggleImportTemplateModal}
          userRole={userRole}
        />
      )}

      {/* Task Modal - stays open when subtask modal opens on top */}
      <CreateEditTaskModal
        mode="task"
        task={task}
        tasks={tasks}
        events={events}
        project={project}
        successTask={successTask}
        isOpen={showNewTaskModal}
        projectId={projectId || id}
        onCreateSubtaskClick={(parentTask) => handleCreateSubtask(parentTask)}
        onClose={() => {
          setShowNewTaskModal(false);
          setTask(null);
        }}
      />

      {/* Subtask Modal - opens on top of task modal (narrower) */}
      <CreateEditTaskModal
        mode="subtask"
        task={subtaskParentTask}
        tasks={tasks}
        events={events}
        project={project}
        successTask={successSubtask}
        isOpen={showNewSubtaskModal}
        projectId={projectId || id}
        onClose={() => {
          setShowNewSubtaskModal(false);
          setSubtaskParentTask(null);
        }}
      />

      <CreateEditEventModal
        event={event}
        tasks={tasks}
        projectId={projectId!}
        isOpen={isEventModalOpen}
        onSuccess={handleEventSuccess}
        onClose={() => setIsEventModalOpen(false)}
      />

      <TaskDetailModal
        task={task}
        project={project!}
        isTaskDetailModal={showTaskDetailModal}
        setIsTaskDetailModal={setShowTaskDetailModal}
      />

      {/* Project Completion Confirmation Modal */}
      <ConfirmationModal
        variant="success"
        closeOnEscape={false}
        title="Complete Project"
        closeOnOverlayClick={false}
        onConfirm={handleProjectCompletion}
        isOpen={showProjectCompletionModal}
        confirmText="Mark Project as Complete"
        onClose={handleProjectCompletionCancel}
        description="All tasks in this project have been completed. Would you like to mark the entire project as completed?"
      />

      {/* Task Template Creation Modals */}
      <SelectTasksModal
        isOpen={showSelectTasksModal}
        onClose={() => {
          setShowSelectTasksModal(false);
          setTemplateProjectId(null);
        }}
        tasks={tasks || []}
        onConfirm={handleTasksSelected}
        title="Select Tasks for Template"
        confirmText="Create Template"
      />

      <CreateTaskTemplateModal
        isOpen={showCreateTemplateModal}
        onClose={() => {
          setShowCreateTemplateModal(false);
          setSelectedTaskIds([]);
          setTemplateProjectId(null);
        }}
        selectedTaskIds={selectedTaskIds}
        projectId={templateProjectId || id || ''}
        tasks={tasks || []}
        onSuccess={handleTemplateCreated}
      />

      <ImportTaskTemplateModal
        isOpen={showImportTemplateModal}
        onClose={() => {
          setShowImportTemplateModal(false);
          setTemplateProjectId(null);
        }}
        projectId={templateProjectId || id || ''}
        projectStartDate={project?.startDate || undefined}
        projectEndDate={project?.endDate || undefined}
        onSuccess={() => {
          setShowImportTemplateModal(false);
          setTemplateProjectId(null);
          if (projectId || id) {
            fetchTasks(projectId || id || '');
          }
        }}
      />

      <TaskCategoriesSlideModal
        isOpen={showTaskCategoriesModal}
        onClose={() => setShowTaskCategoriesModal(false)}
      />
    </div>
  );
};

export default SchedulePage;
